/**
 * Hive Flows — hub (list + create + templates), at ``/workspace/flows``.
 *
 * Index route under the ``flows`` layout (which renders the <Outlet/> for the
 * editor at ``/workspace/flows/$flowId``). Lists the user's flows and creates a
 * fresh one (→ navigates into the visual editor).
 */

import { type Flow, type FlowTemplate, type TeamFlow, runFlow } from "@ando/core";
import {
  ListItemEyebrow,
  ListItemRow,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SurfaceEmptyState,
  SurfaceHeader,
  SurfaceLayout,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  toast,
} from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Copy,
  Lock,
  MoreVertical,
  Pencil,
  Play,
  Plus,
  Share2,
  Sparkles,
  SquarePen,
  Trash2,
  Users,
  Workflow,
} from "lucide-react";
import * as React from "react";

import { PendingApprovalsBanner } from "@/features/hive-flows/components/PendingApprovalsBanner";
import { TemplatesGallery } from "@/features/hive-flows/components/TemplatesGallery";
import {
  useCreateFlow,
  useDeleteFlow,
  useDuplicateFlow,
  useFlowsList,
  useShareFlow,
  useTeamFlows,
  useUpdateFlow,
} from "@/features/hive-flows/hooks/useFlows";
import { flowsListQueryOptions } from "@/features/hive-flows/hooks/useFlows";
import { formatRelativeTime } from "@/features/workspace/hooks/formatRelativeTime";

export const Route = createFileRoute("/_authed/workspace/flows/")({
  // Warm the flows list on hover/focus so the hub paints from cache.
  loader: ({ context: { queryClient } }) => {
    void queryClient.ensureQueryData(flowsListQueryOptions());
  },
  component: FlowsHubRoute,
});

const STATUS_LABEL_MSG: Record<string, ReturnType<typeof msg>> = {
  draft: msg`Bozza`,
  active: msg`Attivo`,
  paused: msg`In pausa`,
  archived: msg`Archiviato`,
};

const SCHEDULE_LABEL_MSG: Record<string, ReturnType<typeof msg>> = {
  manual: msg`Manuale`,
  cron: msg`Pianificato`,
  interval: msg`A intervalli`,
  once: msg`Una volta`,
  webhook: msg`Webhook`,
  reactive: msg`Reattivo`,
};

function FlowsHubRoute(): React.JSX.Element {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const { flows, isLoading } = useFlowsList();
  const { flows: teamFlows, isLoading: isTeamLoading } = useTeamFlows();
  const create = useCreateFlow();
  const del = useDeleteFlow();
  const update = useUpdateFlow();
  const share = useShareFlow();
  const fork = useDuplicateFlow();
  const [tab, setTab] = React.useState<"mine" | "team">("mine");

  const handleRename = React.useCallback(
    (flowId: string, name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      update.mutate(
        { flowId, payload: { name: trimmed } },
        { onError: () => toast.error(i18n._(msg`Rinomina non riuscita`)) },
      );
    },
    [update, i18n],
  );

  const handleDelete = React.useCallback(
    (flow: Flow) => {
      if (
        !window.confirm(i18n._(msg`Eliminare il flow «${flow.name}»? L'azione non è reversibile.`))
      )
        return;
      del.mutate(flow.flow_id, {
        onSuccess: () => toast.success(i18n._(msg`Flow eliminato`)),
        onError: () => toast.error(i18n._(msg`Eliminazione non riuscita`)),
      });
    },
    [del, i18n],
  );

  const handleRun = React.useCallback(
    async (flow: Flow) => {
      try {
        const run = await runFlow(flow.flow_id, { dry_run: false });
        navigate({
          to: "/workspace/flows/$flowId/run",
          params: { flowId: flow.flow_id },
          search: { focus: run.run_id },
        });
      } catch {
        toast.error(i18n._(msg`Esecuzione non riuscita`));
      }
    },
    [navigate, i18n],
  );

  const handleDuplicate = React.useCallback(
    (flow: Flow) => {
      create.mutate(
        { name: i18n._(msg`${flow.name} (copia)`), graph: flow.graph },
        {
          onSuccess: () => toast.success(i18n._(msg`Flow duplicato`)),
          onError: () => toast.error(i18n._(msg`Duplicazione non riuscita`)),
        },
      );
    },
    [create, i18n],
  );

  const handleCreate = React.useCallback(() => {
    create.mutate(
      { name: i18n._(msg`Nuovo flow`) },
      {
        onSuccess: (flow) =>
          navigate({ to: "/workspace/flows/$flowId", params: { flowId: flow.flow_id } }),
        onError: () => toast.error(i18n._(msg`Creazione non riuscita`)),
      },
    );
  }, [create, navigate, i18n]);

  const handleUseTemplate = React.useCallback(
    (template: FlowTemplate) => {
      create.mutate(
        { name: template.name, graph: template.graph },
        {
          onSuccess: (flow) =>
            navigate({ to: "/workspace/flows/$flowId", params: { flowId: flow.flow_id } }),
          onError: () => toast.error(i18n._(msg`Creazione dal modello non riuscita`)),
        },
      );
    },
    [create, navigate, i18n],
  );

  // Toggle a flow's team visibility (owner-only). Quick two-state toggle:
  // any shared state (``org`` or ``project``) -> ``private``; ``private`` ->
  // ``org``. Never silently reassigns a project-scoped share to org-wide —
  // that scope change belongs to the explicit 3-way picker (FlowShareControl,
  // in the flow editor toolbar). Issue #936, extended #1103.
  const handleShare = React.useCallback(
    (flow: Flow) => {
      const next = flow.visibility === "private" ? "org" : "private";
      share.mutate(
        { flowId: flow.flow_id, visibility: next },
        {
          onSuccess: () =>
            toast.success(
              next === "org" ? i18n._(msg`Condiviso col team`) : i18n._(msg`Reso privato`),
            ),
          onError: () => toast.error(i18n._(msg`Operazione non riuscita`)),
        },
      );
    },
    [share, i18n],
  );

  // Fork a COLLEAGUE's shared flow into a new, independently-owned draft
  // (distinct from ``handleDuplicate`` — self-copy). Lands in the editor with
  // the connector nodes flagged via ``?review=`` so the new owner reconciles
  // the accounts before activating. Issue #936.
  const handleUseSharedFlow = React.useCallback(
    (flow: TeamFlow) => {
      fork.mutate(flow.flow_id, {
        onSuccess: (result) => {
          toast.success(i18n._(msg`Flow duplicato: verifica gli account collegati`));
          const review = result.reconciliation_nodes.map((n) => n.node_id).join(",");
          navigate({
            to: "/workspace/flows/$flowId",
            params: { flowId: result.flow_id },
            search: (review ? { review } : {}) as never,
          });
        },
        onError: () => toast.error(i18n._(msg`Duplicazione non riuscita`)),
      });
    },
    [fork, navigate, i18n],
  );

  return (
    <SurfaceLayout
      header={
        <SurfaceHeader
          title={i18n._(msg`I tuoi flow`)}
          meta={
            <span className="text-ink-3">
              <Trans>Automazioni visuali, a concetti — niente codice.</Trans>
            </span>
          }
          actions={
            <button
              type="button"
              onClick={handleCreate}
              disabled={create.isPending}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 font-medium text-[12px] text-white transition-colors",
                "bg-ando-orange hover:bg-ando-orange/90 disabled:opacity-50",
              )}
            >
              <Plus size={14} aria-hidden />
              <Trans>Crea flow</Trans>
            </button>
          }
        />
      }
    >
      {isLoading ? (
        <div className="flex h-full items-center justify-center text-[13px] text-ink-3">
          <Trans>Caricamento…</Trans>
        </div>
      ) : (
        <Tabs value={tab} onValueChange={(v) => setTab(v as "mine" | "team")} className="pt-1">
          <TabsList className="h-9 self-start">
            <TabsTrigger value="mine" className="gap-1.5">
              <Sparkles size={13} strokeWidth={1.75} aria-hidden />
              <Trans>Miei</Trans>
            </TabsTrigger>
            <TabsTrigger value="team" className="gap-1.5">
              <Users size={13} strokeWidth={1.75} aria-hidden />
              <Trans>Team</Trans>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="mine">
            <PendingApprovalsBanner />
            <TemplatesGallery onUse={handleUseTemplate} busy={create.isPending} />
            {flows.length === 0 ? (
              <EmptyState onCreate={handleCreate} busy={create.isPending} />
            ) : (
              <div className="overflow-hidden rounded-[14px] border border-hairline/60">
                {/* Sticky column header — dense list on cream (no card grid). */}
                <div className="sticky top-0 z-10 flex items-center gap-3 border-hairline/60 border-b bg-cream/95 px-8 py-2">
                  <span className="w-7 shrink-0" aria-hidden />
                  <span className="flex-1 font-semibold text-[10px] text-ink-3 uppercase tracking-[0.05em]">
                    <Trans>Flow</Trans>
                  </span>
                  <span className="shrink-0 font-semibold text-[10px] text-ink-3 uppercase tracking-[0.05em]">
                    <Trans>Ultima esecuzione</Trans>
                  </span>
                </div>
                <ul className="flex flex-col" aria-label={i18n._(msg`I tuoi flow`)}>
                  {flows.map((flow, idx) => (
                    <FlowRow
                      key={flow.flow_id}
                      flow={flow}
                      index={idx}
                      onOpen={() =>
                        navigate({
                          to: "/workspace/flows/$flowId",
                          params: { flowId: flow.flow_id },
                        })
                      }
                      onRun={() => handleRun(flow)}
                      onShare={() => handleShare(flow)}
                      onDuplicate={() => handleDuplicate(flow)}
                      onRename={(name) => handleRename(flow.flow_id, name)}
                      onDelete={() => handleDelete(flow)}
                    />
                  ))}
                </ul>
              </div>
            )}
          </TabsContent>

          <TabsContent value="team">
            <TeamFlowsList
              flows={teamFlows}
              isLoading={isTeamLoading}
              busy={fork.isPending}
              onUse={handleUseSharedFlow}
            />
          </TabsContent>
        </Tabs>
      )}
    </SurfaceLayout>
  );
}

function FlowRow({
  flow,
  index,
  onOpen,
  onRun,
  onShare,
  onDuplicate,
  onRename,
  onDelete,
}: {
  flow: Flow;
  index: number;
  onOpen: () => void;
  onRun: () => void;
  onShare: () => void;
  onDuplicate: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
}): React.JSX.Element {
  const { i18n } = useLingui();
  const statusMsg = STATUS_LABEL_MSG[flow.status] ?? STATUS_LABEL_MSG.draft;
  const scheduleMsg = SCHEDULE_LABEL_MSG[flow.schedule_type] ?? SCHEDULE_LABEL_MSG.manual;
  const nodeCount = flow.graph?.nodes?.length ?? 0;
  const stepsLabel =
    nodeCount === 0
      ? i18n._(msg`Flow vuoto`)
      : nodeCount === 1
        ? i18n._(msg`${nodeCount} passo`)
        : i18n._(msg`${nodeCount} passi`);
  const lastRun = formatRelativeTime(flow.last_run_at);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [renaming, setRenaming] = React.useState(false);
  const [draft, setDraft] = React.useState(flow.name);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const close = () => setMenuOpen(false);

  React.useEffect(() => {
    if (renaming) inputRef.current?.select();
  }, [renaming]);

  const commitRename = () => {
    setRenaming(false);
    if (draft.trim() && draft.trim() !== flow.name) onRename(draft);
    else setDraft(flow.name);
  };

  const avatar = (
    <span
      aria-hidden
      className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center text-ink-3"
    >
      <Workflow size={16} />
    </span>
  );

  const eyebrow = (
    <ListItemEyebrow>
      {statusMsg ? i18n._(statusMsg) : ""}
      {" · "}
      {scheduleMsg ? i18n._(scheduleMsg) : ""}
      {" · "}
      {stepsLabel}
      {flow.total_runs > 0 ? i18n._(msg` · ${flow.total_runs} esecuzioni`) : ""}
      {flow.visibility === "org" || flow.visibility === "project" ? (
        <>
          {" · "}
          <span className="text-ando-orange">
            {flow.visibility === "org" ? <Trans>Team</Trans> : <Trans>Progetto</Trans>}
          </span>
        </>
      ) : null}
    </ListItemEyebrow>
  );

  const actions = (
    <Popover open={menuOpen} onOpenChange={setMenuOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={i18n._(msg`Azioni per ${flow.name}`)}
          aria-haspopup="menu"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen(true);
          }}
          className="flex h-6 w-6 items-center justify-center rounded-md text-ink-3 transition-colors hover:bg-ink-1/[0.06] hover:text-ink-1"
        >
          <MoreVertical size={14} aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" role="menu" className="w-44 p-1">
        <FlowMenuItem
          icon={SquarePen}
          label={i18n._(msg`Apri`)}
          onSelect={() => {
            onOpen();
            close();
          }}
        />
        <FlowMenuItem
          icon={Play}
          label={i18n._(msg`Esegui ora`)}
          onSelect={() => {
            onRun();
            close();
          }}
        />
        <FlowMenuItem
          icon={Pencil}
          label={i18n._(msg`Rinomina`)}
          onSelect={() => {
            setDraft(flow.name);
            setRenaming(true);
            close();
          }}
        />
        <FlowMenuItem
          icon={Copy}
          label={i18n._(msg`Duplica`)}
          onSelect={() => {
            onDuplicate();
            close();
          }}
        />
        <FlowMenuItem
          icon={flow.visibility !== "private" ? Lock : Share2}
          label={
            flow.visibility !== "private"
              ? i18n._(msg`Rendi privato`)
              : i18n._(msg`Condividi col team`)
          }
          onSelect={() => {
            onShare();
            close();
          }}
        />
        <hr className="my-1 h-px border-0 bg-hairline" />
        <FlowMenuItem
          icon={Trash2}
          label={i18n._(msg`Elimina`)}
          danger
          onSelect={() => {
            onDelete();
            close();
          }}
        />
      </PopoverContent>
    </Popover>
  );

  // While renaming, render a bespoke non-button row so the text input isn't
  // nested inside ListItemRow's clickable <button> (invalid + would swallow
  // focus). Mirrors the row chrome so the swap is seamless.
  if (renaming) {
    return (
      <li className="relative flex items-center gap-3 border-black/5 border-b bg-white px-8 py-3.5 last:border-b-0">
        {avatar}
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitRename();
            else if (e.key === "Escape") {
              setRenaming(false);
              setDraft(flow.name);
            }
          }}
          aria-label={i18n._(msg`Rinomina ${flow.name}`)}
          className="min-w-0 flex-1 rounded-md border border-ando-orange/50 bg-cream px-1.5 py-0.5 font-medium text-[13px] text-ink-1 outline-none"
        />
      </li>
    );
  }

  return (
    <ListItemRow
      itemId={flow.flow_id}
      index={index}
      avatar={avatar}
      eyebrow={eyebrow}
      title={flow.name}
      trailing={
        lastRun ? <span className="text-[10px] text-ink-3 tabular-nums">{lastRun}</span> : null
      }
      actions={actions}
      actionLabel={i18n._(msg`Apri ${flow.name}`)}
      onClick={onOpen}
    />
  );
}

function FlowMenuItem({
  icon: Icon,
  label,
  onSelect,
  danger,
}: {
  icon: typeof Workflow;
  label: string;
  onSelect: () => void;
  danger?: boolean;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px]",
        danger
          ? "font-medium text-rose-600 hover:bg-rose-500/10"
          : "text-ink-1 hover:bg-ink-1/[0.05]",
      )}
    >
      <Icon size={13} className={danger ? undefined : "text-ink-3"} aria-hidden />
      {label}
    </button>
  );
}

// Team catalog (issue #936) — colleagues' shared flows, rendered as the SAME
// dense list as "Miei" (not a card grid — Andrea's Jul 9 rule for scalable
// collections). Each row forks into an independently-owned copy via "Usa".
function TeamFlowsList({
  flows,
  isLoading,
  busy,
  onUse,
}: {
  flows: TeamFlow[];
  isLoading: boolean;
  busy: boolean;
  onUse: (flow: TeamFlow) => void;
}): React.JSX.Element {
  const { i18n } = useLingui();
  if (isLoading) {
    return (
      <div className="flex items-center justify-center pt-10 text-[13px] text-ink-3">
        <Trans>Caricamento…</Trans>
      </div>
    );
  }
  if (flows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 pt-12 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-ando-orange-soft text-ando-orange">
          <Users size={26} aria-hidden />
        </span>
        <div>
          <p className="font-medium text-[15px] text-ink-1">
            <Trans>Nessun flow condiviso, per ora</Trans>
          </p>
          <p className="mx-auto mt-1 max-w-sm text-[12.5px] text-ink-3">
            <Trans>
              Quando un collega condivide un flow col team lo trovi qui: puoi farne una tua copia e
              usarlo subito.
            </Trans>
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-[14px] border border-hairline/60">
      <ul className="flex flex-col" aria-label={i18n._(msg`Flow condivisi dal team`)}>
        {flows.map((flow, idx) => (
          <TeamFlowRow
            key={flow.flow_id}
            flow={flow}
            index={idx}
            busy={busy}
            onUse={() => onUse(flow)}
          />
        ))}
      </ul>
    </div>
  );
}

function TeamFlowRow({
  flow,
  index,
  busy,
  onUse,
}: {
  flow: TeamFlow;
  index: number;
  busy: boolean;
  onUse: () => void;
}): React.JSX.Element {
  const { i18n } = useLingui();
  const scheduleMsg = SCHEDULE_LABEL_MSG[flow.schedule_type] ?? SCHEDULE_LABEL_MSG.manual;
  const sharedBy = flow.shared_by_name?.trim();
  const projectName = flow.visibility === "project" ? flow.project_name?.trim() : null;
  const avatar = (
    <span
      aria-hidden
      className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center text-ink-3"
    >
      <Workflow size={16} />
    </span>
  );
  const eyebrow = (
    <ListItemEyebrow>
      {sharedBy ? i18n._(msg`Condiviso da ${sharedBy}`) : i18n._(msg`Condiviso dal team`)}
      {" · "}
      {scheduleMsg ? i18n._(scheduleMsg) : ""}
      {" · "}
      <span className="text-ando-orange">
        {projectName ? i18n._(msg`Progetto: ${projectName}`) : <Trans>Team</Trans>}
      </span>
    </ListItemEyebrow>
  );
  const useButton = (
    <button
      type="button"
      disabled={busy}
      onClick={(e) => {
        e.stopPropagation();
        onUse();
      }}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1 font-medium text-[12px] transition-colors",
        "border-[1.5px] border-ando-orange/70 text-ando-orange hover:bg-ando-orange/10 disabled:opacity-50",
      )}
    >
      <Copy size={13} aria-hidden />
      <Trans>Usa</Trans>
    </button>
  );
  return (
    <ListItemRow
      itemId={flow.flow_id}
      index={index}
      avatar={avatar}
      eyebrow={eyebrow}
      title={flow.name}
      actions={useButton}
      actionLabel={i18n._(msg`Usa ${flow.name}`)}
      onClick={onUse}
    />
  );
}

function EmptyState({
  onCreate,
  busy,
}: { onCreate: () => void; busy: boolean }): React.JSX.Element {
  const { i18n } = useLingui();
  return (
    <SurfaceEmptyState
      icon={<Workflow size={18} strokeWidth={1.75} aria-hidden />}
      title={i18n._(msg`Crea la tua prima automazione`)}
      description={i18n._(
        msg`Costruisci flussi visuali tra tutti i tuoi strumenti — a concetti, non a logiche tecniche.`,
      )}
      action={
        <button
          type="button"
          onClick={onCreate}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-md bg-ando-orange px-4 py-2 font-medium text-[13px] text-white transition-colors hover:bg-ando-orange/90 disabled:opacity-50"
        >
          <Plus size={15} aria-hidden />
          <Trans>Crea flow</Trans>
        </button>
      }
    />
  );
}
